import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import axios from 'axios';

const IndustrySelection = ({ value, onChange }) => {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/industries');
      setIndustries(response.data.industries);
      setError(null);
    } catch (err) {
      setError('Failed to load industries');
      console.error('Error fetching industries:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (industry) => {
    onChange(industry.slug);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Choose Your Industry
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select the industry that best matches your business
      </Typography>

      <Grid container spacing={2}>
        {industries.map((industry) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={industry.slug}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: value === industry.slug ? 3 : 1,
                borderColor: value === industry.slug ? 'primary.main' : 'divider',
                backgroundColor: value === industry.slug ? 'primary.50' : 'background.paper',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3,
                  borderColor: 'primary.main'
                }
              }}
              onClick={() => handleSelect(industry)}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h2" sx={{ mb: 1 }}>
                  {industry.icon}
                </Typography>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {industry.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {industry.description}
                </Typography>
                {industry.hasSpecializations && (
                  <Typography 
                    variant="caption" 
                    color="primary" 
                    sx={{ mt: 1, display: 'block', fontWeight: 'medium' }}
                  >
                    Multiple specializations available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {value && (
        <Alert severity="success" sx={{ mt: 3 }}>
          Selected: <strong>{industries.find(i => i.slug === value)?.name || value}</strong>
        </Alert>
      )}
    </Box>
  );
};

export default IndustrySelection;